import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useRef, useState } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import EmojiPicker, { EmojiPickerPopOver } from '../../components/EmojiPicker';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import reactionIcon from '../../icons/non-prefilled/16x16/reaction.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import userPath from '../../utils/url/userPath';
import useIsPublicView from '../../utils/useIsPublicView';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import isContentEmpty from '../utils/isContentEmpty';
import * as styles from './Comment.module.scss';
import CommentAuthor from './CommentAuthor';
import CommentInput from './CommentInput';
import CommentMenu from './CommentMenu';
import CommentPreview from './CommentPreview';
import CommentReactionList from './CommentReactionList';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
} from './MagneticWallItemOverflowMenu';
import showFullPageSheet from './showFullPageSheet';
import useCommentActions from './useCommentActions';
import Link from '../../utils/router/Link';

const COMMENT_META_MARGIN_BOTTOM = parseInt(styles.commentMetaMarginBottom, 10);

const POST_FRAGMENT = gql(`
  fragment Comment_post on RootPostFields {
    id
    ...useCommentActions_post
  }
`);

const MAX_LINE_COUNT = 3;
const LARGE_COMMENT_LENGTH_THRESHOLD = 400;

const COMMENT_FRAGMENT = gql(`
  fragment Comment_comment on PublicCommentFields {
    id
    content
    author {
      id
      ...userPath_user
      ...MagneticWallItemAvatar_user
      ...CommentAuthor_author
    }
    reactions {
      ...CommentReactionList_reactions
    }
    insertedAt
    updatedAt
  }
`);

export const COMMENT_ANIMATION_DURATION = 0.2;

interface CommentProps {
  comment: DocumentType<typeof COMMENT_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  onResize: (avoidRepositioningFocusedThread?: boolean) => void;
  onResolve: () => void;
  threadId: string;
  first: boolean;
  last: boolean;
  expanded: boolean;
  initialShowingMore?: boolean;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  folded?: boolean;
  shouldHideToolbar?: (offsetTop: number) => boolean;
  disabledAnimatedPreview?: boolean;
  threadHovered?: boolean;
}

const Comment = ({
  comment,
  onResize,
  onResolve,
  threadId,
  post,
  first,
  last,
  expanded,
  editing,
  onEdit,
  onCancelEdit,
  folded,
  threadHovered,
}: CommentProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const {
    deleteComment,
    updateComment,
    toggleCommentReaction,
    copyThreadLink,
    deleteThread,
  } = useCommentActions(threadId, post, comment);

  const [isSaving, setIsSaving] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [editContent, setEditContent] = useState<Op[] | null>(null);
  const commentContentRef = useRef<HTMLDivElement>(null);

  const currentUser = useAppSelector((state) => state.session.user);
  const isFullPageSheet = showFullPageSheet();
  const isPublicView = useIsPublicView();

  if (editing && !editContent) {
    setEditContent(comment.content);
  }

  const onEditChange = (newContent: Op[]) => {
    setEditContent(newContent);
    onResize(true);
  };

  const onEmojiPickerOpenChange = (open: boolean) => {
    setIsEmojiPickerOpen(open);
  };

  const onSave = async () => {
    if (!editContent?.length) {
      return;
    }

    setIsSaving(true);

    try {
      await updateComment(JSON.stringify(editContent));
      onCancelEdit();
    } catch {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, we could not save the comment. Please try again or contact support.',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getCommentMenuItems = (includingReactionItem: boolean) => {
    const isAuthor = comment.author.id === currentUser.id;
    const menuItems = [];

    if (first && isFullPageSheet) {
      menuItems.push(
        <MenuItem key="resolve" icon={checkIcon} onClick={onResolve}>
          Resolve
        </MenuItem>,
      );
    }

    if (isAuthor) {
      menuItems.push(
        <MenuItem key="edit" icon={editIcon} onClick={onEdit}>
          Edit comment
        </MenuItem>,
      );
    }

    if (!isFullPageSheet || first) {
      menuItems.push(
        <MenuItem key="copy" icon={linkIcon} onClick={copyThreadLink}>
          Copy link to thread
        </MenuItem>,
      );
    }

    if (includingReactionItem) {
      menuItems.push(
        <MenuItem
          key="reaction"
          icon={reactionIcon}
          onClick={() => onEmojiPickerOpenChange(true)}
        >
          Add reaction
        </MenuItem>,
      );
    }

    if (isAuthor) {
      if (menuItems.length > 0) {
        menuItems.push(<MenuSeparator key="separator" />);
      }
      if (first) {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete_thread"
            danger
            onClick={deleteThread}
          >
            Delete thread
          </MenuItem>,
        );
      } else {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete"
            danger
            onClick={deleteComment}
          >
            Delete comment
          </MenuItem>,
        );
      }
    }

    return menuItems;
  };

  const avatar = <MagneticWallItemAvatar user={comment.author} />;
  const commonButtonProps = {
    emojiPickerOpen: isEmojiPickerOpen,
    onEmojiPickerOpenChange,
    toggleCommentReaction,
  };

  const isToolbarEnabled =
    !editing &&
    ((expanded && (isFullPageSheet || isHovered)) || (first && threadHovered));

  return (
    <div
      ref={elementRef}
      className={classNames('comment', styles.comment, {
        'comment-large':
          expanded &&
          new Delta(comment.content).length() > LARGE_COMMENT_LENGTH_THRESHOLD,
        [styles.editing]: editing,
        [styles.first]: first,
        [styles.last]: last,
      })}
      {...addTestId(`comment-${comment.id}`)}
    >
      <div
        className={classNames('comment-left', styles.left, {
          [styles.firstFolded]: folded && first,
          [styles.lastFolded]: folded && last,
        })}
      >
        <div className={styles.lineTop} />
        {expanded && !isPublicView ? (
          <Link to={userPath(comment.author)} target="_blank" rel="noreferrer">
            {avatar}
          </Link>
        ) : (
          avatar
        )}
        <div className={styles.lineBottom} />
      </div>
      <div
        className={classNames('comment-right', {
          'comment-right-sheet': isFullPageSheet,
        })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AnimatePresence initial={false}>
          {expanded && !editing && (
            <OpacityMotionTransition
              key="comment-meta"
              animate={{
                height: 'auto',
                marginBottom: COMMENT_META_MARGIN_BOTTOM,
              }}
              initial={{ height: 0, marginBottom: COMMENT_META_MARGIN_BOTTOM }}
              exit={{ height: 0, marginBottom: 0 }}
              className="comment-meta"
              transition={{ duration: COMMENT_ANIMATION_DURATION }}
              onAnimationComplete={() => onResize()}
            >
              <CommentAuthor
                isFirst={first}
                isExpanded
                author={comment.author}
                insertedAt={comment.insertedAt}
                updatedAt={comment.updatedAt}
                copyThreadLink={copyThreadLink}
              />
            </OpacityMotionTransition>
          )}
        </AnimatePresence>
        <div
          className={classNames('comment-content', styles.commentContent)}
          ref={commentContentRef}
          {...addTestId('commentContent')}
        >
          {editing && editContent ? (
            <CommentInput
              autoFocus
              initialContent={editContent}
              onChange={onEditChange}
              onSave={onSave}
            />
          ) : (
            <CommentPreview
              initialHeight={commentContentRef.current?.clientHeight}
              collapseLines={!expanded}
              content={comment.content}
              link={expanded}
              showHoverCard={expanded}
              maxLines={MAX_LINE_COUNT}
              disableLineClamp={expanded}
              onResize={onResize}
            />
          )}
        </div>
        <AnimatePresence>
          {isToolbarEnabled && (
            <OpacityMotionTransition
              key="comment-button"
              className="comment-button"
              transition={{ duration: COMMENT_ANIMATION_DURATION }}
            >
              {isFullPageSheet ? (
                <CommentMenu
                  menuItems={getCommentMenuItems(true)}
                  {...commonButtonProps}
                />
              ) : (
                <MagneticWallItemOverflowMenu
                  menuItems={getCommentMenuItems(false)}
                >
                  {first && (
                    <Tooltip content="Resolve thread">
                      <MagneticWallOverflowButton
                        onClick={onResolve}
                        icon={checkIcon}
                        ariaLabel="Resolve thread"
                      />
                    </Tooltip>
                  )}
                  <EmojiPickerPopOver
                    open={isEmojiPickerOpen}
                    placement="bottom-end"
                    setOpen={onEmojiPickerOpenChange}
                    offset="small"
                    target={
                      <MagneticWallOverflowButton
                        icon={reactionIcon}
                        active={isEmojiPickerOpen}
                        onClick={() =>
                          onEmojiPickerOpenChange(!isEmojiPickerOpen)
                        }
                      />
                    }
                  >
                    <EmojiPicker
                      onPick={(emoji) => {
                        onEmojiPickerOpenChange(false);
                        toggleCommentReaction(emoji);
                      }}
                    />
                  </EmojiPickerPopOver>
                </MagneticWallItemOverflowMenu>
              )}
            </OpacityMotionTransition>
          )}
          {expanded && editing && (
            <OpacityMotionTransition
              key="comment-actions"
              className="comment-actions"
              transition={{ duration: COMMENT_ANIMATION_DURATION }}
              animate={{ height: 'auto', marginTop: 12 }}
              initial={{ height: 0, marginTop: 0 }}
              exit={{ height: 0, marginTop: 0 }}
            >
              <div className={styles.editingActions}>
                <Button variant="secondary" onClick={onCancelEdit} size="small">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={isSaving || isContentEmpty(editContent)}
                  onClick={onSave}
                  size="small"
                >
                  Save
                </Button>
              </div>
            </OpacityMotionTransition>
          )}
          {expanded &&
            !editing &&
            comment.reactions &&
            comment.reactions.length > 0 && (
              <OpacityMotionTransition
                key="comment-reactions"
                className="comment-reactions"
                transition={{ duration: COMMENT_ANIMATION_DURATION }}
                animate={{ height: 'auto' }}
                initial={{ height: 0 }}
                exit={{ height: 0 }}
                onAnimationComplete={() => onResize()}
              >
                <CommentReactionList
                  reactions={comment.reactions}
                  toggleCommentReaction={toggleCommentReaction}
                />
              </OpacityMotionTransition>
            )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Comment;
